import { useState, useEffect, useRef } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import './SetInstantAvailability.css';
import successIcon from '../../../assets/Model/approve-icon.svg';
import { IoClose } from 'react-icons/io5';

const SetInstantAvailability = ({
  isOpen,
  onClose,
  onConfirm,
  isAvailable: externalAvailable,
  duration,
  timeRemaining: externalTimeRemaining,
  onTimerEnd,
}) => {
  const [selectedHours, setSelectedHours] = useState(duration || 2);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAvailable, setIsAvailable] = useState(externalAvailable || false);
  const [timeRemaining, setTimeRemaining] = useState(externalTimeRemaining || 0);
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);

  const hourOptions = [2, 3, 4];

  useEffect(() => {
    // Update internal state when prop changes
    setIsAvailable(externalAvailable || false);
  }, [externalAvailable]);

  useEffect(() => {
    // Update selected hours when duration prop changes
    if (duration) {
      setSelectedHours(duration);
    }
  }, [duration]);

  useEffect(() => {
    // Update timeRemaining when external value changes
    if (externalTimeRemaining !== undefined) {
      setTimeRemaining(externalTimeRemaining);
    }
  }, [externalTimeRemaining]);

  useEffect(() => {
    let timer;
    if (isAvailable && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining(prevTime => {
          if (prevTime <= 1) {
            clearInterval(timer);
            setIsAvailable(false);
            // Call the onTimerEnd callback when timer reaches zero
            if (onTimerEnd) {
              onTimerEnd();
            }
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [isAvailable, timeRemaining, onTimerEnd]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // When component mounts and isAvailable is true but timeRemaining is 0,
    // set the timer based on selected hours
    if (isAvailable && timeRemaining === 0) {
      setTimeRemaining(selectedHours * 60 * 60);
    }
  }, [isAvailable, selectedHours, timeRemaining]);

  const handleSetAvailability = () => {
    onConfirm && onConfirm(selectedHours);
    onClose();
  };

  const formatTime = seconds => {
    if (seconds <= 0) return '00:00:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // If directly showing timer (from prop)
  if (externalAvailable) {
    return (
      <div className="availability-timer" style={{ fontWeight: 'bold' }}>
        {formatTime(timeRemaining)}
      </div>
    );
  }

  // Modal is closed and not available
  if (!isOpen && !isAvailable) return null;

  // Available but showing as timer only
  if (isAvailable) {
    return (
      <div className="availability-timer" style={{ fontWeight: 'bold' }}>
        {formatTime(timeRemaining)}
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="instant-availability-modal" ref={modalRef}>
        <div className="modal-close">
          <button onClick={onClose}>
            <IoClose size={24} />
          </button>
        </div>

        <div className="modal-content-instant">
          <div className="modal-icon">
            <img
              src={successIcon}
              alt="close"
              style={{ width: '250px', height: '120px', maxWidth: '250px' }}
            />
          </div>

          <h2>Enable Instant Availability</h2>

          <p>
            By enabling instant availability, you're making yourself available for the next{' '}
            {selectedHours} hours. During this time, students can book sessions with you
            immediately.
          </p>

          <div className="availability-settings">
            <label>Set Instant Availability</label>

            <div className="dropdown-container" ref={dropdownRef}>
              <button className="dropdown-button" onClick={() => setShowDropdown(!showDropdown)}>
                <span>{selectedHours} hours</span>
                <IoIosArrowDown />
              </button>

              {showDropdown && (
                <div className="dropdown-list">
                  {hourOptions.map(hours => (
                    <div
                      key={hours}
                      className={`dropdown-item ${selectedHours === hours ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedHours(hours);
                        setShowDropdown(false);
                      }}
                    >
                      {hours} hours
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button className="confirm-availability-button" onClick={handleSetAvailability}>
              Confirm Availability
            </button>

            <button className="dismiss-button-instant" onClick={onClose}>
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetInstantAvailability;
